import { useState, type FormEvent } from "react";
import {
  Button,
  Input,
  useToast,
} from "@/components/ds";
import type { Invite } from "@/domain/team/types";
import styles from "./InviteForm.module.css";

type InviteMode = "link" | "email";

interface InviteFormProps {
  readonly onInviteSent: (invite: Invite) => void;
}

function RolePill({
  label,
  checked,
  onChange,
  disabled,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={`${styles.rolePill} ${checked ? styles.rolePillActive : ""}`}
      onClick={() => onChange(!checked)}
      disabled={disabled}
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" />
        </svg>
      )}
      {label}
    </button>
  );
}

export function InviteForm({ onInviteSent }: InviteFormProps) {
  const { toast } = useToast();
  const [mode, setMode] = useState<InviteMode>("link");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  const [isCleaner, setIsCleaner] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);

  function clearError(field: string) {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErrors({});
    setSending(true);

    const isLink = mode === "link";
    const body = isLink
      ? { isOwner, isCleaner }
      : { name, email, isOwner, isCleaner };

    try {
      const response = await fetch("/api/team/invites", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!data.success) {
        if (data.error?.fields) {
          setErrors(data.error.fields);
        } else {
          toast("error", data.error?.message ?? "Something went wrong. Please try again.");
        }
        return;
      }

      onInviteSent(data.data.invite);

      if (isLink) {
        toast("success", "Link created. Share it with your team.");
      } else {
        toast("success", `Invite sent to ${email}`);
      }

      setName("");
      setEmail("");
      setIsOwner(false);
      setIsCleaner(true);
    } catch {
      toast("error", "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  }

  const rolePills = (
    <div className={styles.roleGroup}>
      <RolePill label="Cleaner" checked={isCleaner} onChange={setIsCleaner} disabled={sending} />
      <RolePill label="Manager" checked={isOwner} onChange={setIsOwner} disabled={sending} />
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.modeToggle} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "link"}
          className={`${styles.modeOption} ${mode === "link" ? styles.modeOptionActive : ""}`}
          onClick={() => { setMode("link"); setErrors({}); }}
        >
          Share a link
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "email"}
          className={`${styles.modeOption} ${mode === "email" ? styles.modeOptionActive : ""}`}
          onClick={() => { setMode("email"); setErrors({}); }}
        >
          By email
        </button>
      </div>

      {mode === "email" ? (
        <>
          <div className={styles.fieldsRow}>
            <div className={styles.stackedField}>
              <label htmlFor="invite-name" className={styles.fieldLabel}>
                Name<span className={styles.required}>*</span>
              </label>
              <Input
                id="invite-name"
                name="name"
                value={name}
                onChange={(e) => { setName(e.target.value); clearError("name"); }}
                error={!!errors.name}
                placeholder="e.g. Sarah Jones"
                disabled={sending}
              />
              {errors.name && <span className={styles.fieldError}>{errors.name}</span>}
            </div>

            <div className={styles.stackedField}>
              <label htmlFor="invite-email" className={styles.fieldLabel}>
                Email<span className={styles.required}>*</span>
              </label>
              <Input
                id="invite-email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearError("email"); }}
                error={!!errors.email}
                placeholder="e.g. sarah@example.com"
                disabled={sending}
              />
              {errors.email && <span className={styles.fieldError}>{errors.email}</span>}
            </div>
          </div>

          <div className={styles.controlsRow}>
            {rolePills}
            <Button type="submit" size="sm" disabled={sending}>
              {sending ? "Sending..." : "Send invite"}
            </Button>
          </div>
        </>
      ) : (
        <div className={styles.linkRow}>
          {rolePills}
          {errors.role && <span className={styles.fieldError}>{errors.role}</span>}
          <Button type="submit" size="sm" disabled={sending}>
            {sending ? "Creating..." : "Create link"}
          </Button>
        </div>
      )}
    </form>
  );
}
